import * as yup from "yup";
import { z } from "zod";

import { CreatePatientSex } from "@elation/api-client/src/stitch-web/models/createPatientSex";

import { NaiveDate } from "@src/shared/utils/types";

export const CreatePatientSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  dob: z.custom<NaiveDate>(
    (value) => value instanceof Date && !Number.isNaN(value.getTime()),
    { error: "Date of birth is required" },
  ),
  sex: z.enum(CreatePatientSex, { error: "Sex at birth is required" }),
});

export type CreatePatient = z.infer<typeof CreatePatientSchema>;

export interface CreatePatientFormValues {
  firstName: string;
  lastName: string;
  dob: string;
  sex: CreatePatient["sex"] | "";
}

export const CreatePatientFormInitialValues: CreatePatientFormValues = {
  firstName: "",
  lastName: "",
  dob: "",
  sex: "",
};

export const CreatePatientFormValidationSchema =
  yup.object<CreatePatientFormValues>({
    firstName: yup.string().trim().required("First name is required"),
    lastName: yup.string().trim().required("Last name is required"),
    dob: yup
      .string()
      .required("Date of birth is required")
      .test("valid-naive-date", "Date of birth is required", (value) => {
        if (!value) return false;

        try {
          NaiveDate.fromString(value);
          return true;
        } catch {
          return false;
        }
      }),
    sex: yup
      .string()
      .oneOf(Object.values(CreatePatientSex), "Sex at birth is required")
      .required("Sex at birth is required") as yup.Schema<
      CreatePatientFormValues["sex"]
    >,
  });

export function createPatientFromFormValues(
  values: CreatePatientFormValues,
): CreatePatient {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    dob: NaiveDate.fromString(values.dob),
    sex: values.sex as CreatePatient["sex"],
  };
}
